import { useEffect, useState } from 'react';
import {
  loadAwaitingApprovalsCounts,
  type ApprovalsCountByPath,
} from './awaitingApprovalsCount';

export interface UseAwaitingApprovalsCountResult {
  byPath: ApprovalsCountByPath;
  total: number;
  loading: boolean;
}

/**
 * Defer the Approvals badge fetch until the browser is idle so it never competes
 * with the page the user just opened. Soft-fails to 0 so a count error cannot
 * take down the navbar.
 */
export function useAwaitingApprovalsCount(
  enabled: boolean,
  paths: string[],
  refreshKey?: string
): UseAwaitingApprovalsCountResult {
  const [byPath, setByPath] = useState<ApprovalsCountByPath>({});
  const [loading, setLoading] = useState(false);
  const pathsKey = paths.join('|');

  useEffect(() => {
    const pathList = pathsKey ? pathsKey.split('|') : [];
    if (!enabled || pathList.length === 0) {
      setByPath({});
      setLoading(false);
      return;
    }

    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      setLoading(true);
      loadAwaitingApprovalsCounts(pathList)
        .then((result) => {
          if (!cancelled) setByPath(result);
        })
        .catch(() => {
          if (!cancelled) setByPath({});
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    const cancelIdle = scheduleIdle(start);
    return () => {
      cancelled = true;
      cancelIdle();
    };
  }, [enabled, pathsKey, refreshKey]);

  const total = Object.values(byPath).reduce((sum, n) => sum + n, 0);
  return { byPath, total, loading };
}

function scheduleIdle(cb: () => void): () => void {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(() => cb(), { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(cb, 0);
  return () => window.clearTimeout(id);
}
